<script setup lang="ts">
import { loginClicked } from "~/analytics/segment";

import type { ClubSsoStoryblok } from "#storyblok-components";

defineProps<{ blok: ClubSsoStoryblok }>();

const clubProvider = useClubSso();
const { isLoggedIn } = useAuth();

const isLoading = ref(false);
const { login, addClubToAutoFollow } = useLoginWithSocials();
const { openOnboardingDialog, onboardingEntry } = useOnboardingDialog();

const onLogin = async () => {
  const startingPoint: OnboardingEntry = {
    type: OnboardingEntryType.ClubSso,
    data: { source: "CLUB_SSO" },
  };

  if (!clubProvider.value) {
    openOnboardingDialog(startingPoint);

    return;
  }

  onboardingEntry.value = startingPoint;
  isLoading.value = true;

  loginClicked({ provider: clubProvider.value.name });

  await addClubToAutoFollow(clubProvider.value.alternativeName);
  login({
    provider: { custom: clubProvider.value.cognitoProviderName },
    redirectBackToCurrentPath: true,
    reopenVerificationsDialog: false,
  });
};
</script>

<template>
  <div
    v-show="!isLoggedIn"
    v-editable="blok"
    class="w-viewport bg-platform-bg-2"
  >
    <div
      class="base-block-spacing flex flex-col md:flex-row gap-6 md:gap-0 md:flex md:justify-between px-8 md:items-center"
    >
      <div class="flex flex-col">
        <h2 class="uppercase font-bold text-3xl sm:text-4xl mb-1">
          {{
            $t("clubSso.loginHeader", {
              PLACEHOLDER_providerName:
                clubProvider?.name ?? $t("clubSso.club"),
            })
          }}
        </h2>

        <p class="text-lg">
          {{
            $t("clubSso.loginDescription", {
              PLACEHOLDER_providerName:
                clubProvider?.name ?? $t("clubSso.club"),
            })
          }}
        </p>
      </div>

      <div>
        <BaseButton
          type="button"
          color-scheme="primary"
          :disabled="isLoading"
          data-test="login-with-club-button"
          @click="onLogin"
        >
          {{ $t("clubSso.continueWithClubAccount") }}
        </BaseButton>
      </div>
    </div>
  </div>
</template>
